import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  X, ArrowLeft,
  Download,
  ExternalLink, Plus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WorkflowListShell from '../components/workflow/WorkflowListShell';
import EngagementProgressBoard from '../components/workflow/EngagementProgressBoard';
import useEngagementListFilters from '../components/shared/useEngagementListFilters';
import { engagementListColumns } from '../components/shared/engagementListColumns';
import { formatDate } from '../utils/dateFormat';
import { engagementDisplayName } from '../utils/engagementName';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { fmtBytes } from '../components/workflow/constants';
import { FilePicker } from '../components/shared/FormKit';
import FileList, { DownloadLink, PreviousVersions } from '../components/shared/FileList';
import EngagementExportModal from '../components/shared/EngagementExportModal';
import { exportExcelBtnCls } from '../components/shared/ExportExcelButton';

// Multipart upload of an engagement-owned attachment. Never
// set Content-Type — the browser adds the multipart boundary itself.
const uploadEngagementAttachment = async (engagementId, file, { docType, replacesSource, replacesFileId } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (docType) fd.append('docType', docType);
  if (replacesSource) {
    fd.append('replacesSource', replacesSource);
    fd.append('replacesFileId', String(replacesFileId));
  }
  const res = await fetch(`${API_BASE_URL}/api/engagements/${engagementId}/attachments`, {
    method: 'POST', credentials: 'include', body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Upload failed (${res.status})`);
    err.code = data.code;
    throw err;
  }
  return data;
};

const fmtDate = (v) => (v ? formatDate(v) : '—');

// ── List page ────────────────────────────────────────────────────────────────
// 用 workflow 的清單殼與樣式。整列點下去以新分頁開
// `/qm/engagements/:id`——engagement 有管理
// 面的網址,workflow 詳情頁的「Open in Management →」不必靠 `?search=<案名>`
// 逼近那一筆（撞名會列出多筆、改名就失效,見 utils/engagementLink）。
//
// 檢視／編輯／刪除／附件／歷程都在詳情頁;這裡只留篩選與導航。
//
// 兩種視圖：List＝逐案表格；Grouped＝跟 Engagement
// Progress 同一張看板（components/workflow/EngagementProgressBoard 共用元件，
// 同集團＋FYE 同年一張卡、六關 chip），差別只在後端帶 scope=manage（憑
// engagements:edit 看全部，跟旁邊的 List 同視野）、案名連到管理詳情頁。
const QMEngagements = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('engagements:edit');
  // 深連結仍可帶 ?search=…（舊連結、書籤）——交給 shell 當初始搜尋。
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const view = searchParams.get('view') === 'grouped' ? 'grouped' : 'list';
  const setView = (v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'grouped') next.set('view', 'grouped');
      else next.delete('view');
      return next;
    }, { replace: true });
  };

  // 工具列跟 Grouped 看板同一套：Status 晶片（預設＝Dropdown Lists 勾的
  // show_by_default）＋集團／FYE 年份／合夥人／服務。四個下拉跟看板是同一個
  // 元件、同一組網址參數、同一個後端收集器，切換 List／Grouped 時內容與條件都不變。
  const engFilters = useEngagementListFilters({ optionsPath: '/api/engagements/options' });
  const extraParams = useMemo(() => ({
    ...(initialSearch ? { search: initialSearch } : {}),
    ...engFilters.params,
  }), [initialSearch, engFilters.params]);

  // List／Grouped 段落切換——兩種視圖各自的工具列都掛同一顆，切換不迷路。
  const viewToggle = (
    <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="group">
      {[['list', 'List'], ['grouped', 'Grouped']].map(([v, label], i) => (
        <button
          key={v}
          type="button"
          onClick={() => setView(v)}
          className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap ${i > 0 ? 'border-l border-slate-300' : ''} ${
            view === v ? 'bg-blue-50 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // 下載：FYE 區間＋Status 在視窗裡選，與上面的篩選列各自獨立。
  const [exporting, setExporting] = useState(false);
  const exportButton = (
    <button
      type="button"
      onClick={() => setExporting(true)}
      disabled={!engFilters.ready}
      className={exportExcelBtnCls}
    >
      <Download className="w-4 h-4" /> Export Excel
    </button>
  );
  const exportModal = exporting && (
    <EngagementExportModal
      statusOptions={engFilters.statusOptions}
      initialStatuses={engFilters.statuses}
      onClose={() => setExporting(false)}
    />
  );

  const toolbarTop = (
    <>
      {engFilters.statusChips}
      <div className="ml-auto flex items-center gap-2">{exportButton}{viewToggle}</div>
    </>
  );

  if (view === 'grouped') {
    // 標頭照 WorkflowListShell 的長相自刻（殼是表格清單專用），看板本體與
    // /workflow/progress 共用同一個元件。
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
        <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Engagements</h1>
          </div>
          <div className="flex items-center gap-2">
          {exportButton}
          {canEdit && (
            <Link
              to="/qm/engagements/new"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> New Engagement
            </Link>
          )}
          </div>
        </div>
        <EngagementProgressBoard
          scope="manage"
          engagementTo={(e) => `/qm/engagements/${e.engagementId}`}
          toolbarExtra={viewToggle}
        />
        {exportModal}
      </div>
    );
  }

  if (!engFilters.ready) return <p className="text-center text-sm text-slate-500 mt-8">Loading…</p>;

  return (
    <>
    <WorkflowListShell
      title="Engagements"
      subtitle="All registered engagements."
      backTo="/manage"
      backLabel="Management"
      canCreate={canEdit}
      newTo="/qm/engagements/new"
      newLabel="New Engagement"
      showHideCompleted={false}
      apiPath="/api/engagements"
      extraParams={extraParams}
      rowTo={(r) => `/qm/engagements/${r.id}`}
      rowNewTab
      searchPlaceholder="Search file ref, engagement, client group, entity, service or description…"
      defaultSort={{ key: 'yearEnd', direction: 'desc' }}
      emptyText="No engagements found"
      toolbarTop={toolbarTop}
      filters={engFilters.selects}
      // 欄位與 My Records 的 Engagements tab 共用——兩面的表格
      // 必須用同一種方式描述一筆 engagement。
      columns={engagementListColumns()}
    />
    {exportModal}
    </>
  );
};

// Doc-type choices when adding a brand-new engagement-owned attachment.
const OWN_DOC_TYPE_OPTIONS = [
  { value: 'SUPPLEMENTARY', label: 'Supplementary' },
  { value: 'QC6', label: 'QC6' },
  { value: 'QC7', label: 'QC7' },
  // A self-held "previous year QC" (the live prev reference is never
  // uploaded here) — same doc type the server whitelists for it.
  { value: 'QC7_PREV', label: 'Previous year QC' },
  { value: 'FS', label: 'Signed Financial Statements' },
  { value: 'QC35', label: 'QC35 Form' },
  // The QM screening document. The copy filed at the
  // Registration's approval is locked with that request, so a correction or a
  // later screening is uploaded here and supersedes it by doc type.
  { value: 'SCREENING', label: 'Screening' },
  // The company's ACRA business profile — same reason: a
  // manually created engagement has no registration to carry one.
  { value: 'BIZFILE', label: 'BizFile' },
];

// The engagement-owned doc-type to use when uploading a new version of an item
// (the workflow original is never touched; the new own file supersedes it, by
// docType). Signed Financial Statements (fs source) maps to 'FS'; QC35 (its own source —
// legacy qc35 rows still ride source 'fs') maps to 'QC35'.
const uploadDocTypeFor = (f) => {
  if (f.source === 'own') return f.docType || 'SUPPLEMENTARY';
  if (['QC6', 'QC7', 'QC7_PREV'].includes(f.docType)) return f.docType;
  if (f.source === 'fs') return f.docType === 'qc35' ? 'QC35' : 'FS';
  if (f.source === 'qc35') return 'QC35';
  return 'SUPPLEMENTARY';
};

// Attachments editor embedded in the engagement Edit modal
// (docs/modules/engagements.md §6). The workflow (申請單) originals are never
// replaced/overwritten — you upload a NEW VERSION, which supersedes the current
// one by doc type; the previous versions (incl. the workflow original) move to a
// collapsible history and stay downloadable. Referenced sources (group
// structure, previous-year QC) are updated at their own source.
//
// EDITING ONLY. The read-only panel is EngagementAttachments, rendered by the
// shared EngagementView — this editor carries no viewOnly mode,
// so the two never drift out of sync by half a branch.
export const AttachmentsEditor = ({ engagementId, onChanged }) => {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addType, setAddType] = useState('SUPPLEMENTARY');

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/engagements/${engagementId}/attachments`);
      setFiles(data.data || []);
    } catch (e) { setError(e.message); }
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (file, opts) => {
    setBusy(true); setError(null);
    try {
      await uploadEngagementAttachment(engagementId, file, opts);
      await load();
      onChanged && onChanged();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  // Upload a new version of an existing document — supersedes the current one.
  const onNewVersion = (row) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) doUpload(file, { docType: uploadDocTypeFor(row), replacesSource: row.source, replacesFileId: row.fileId });
  };
  const onAdd = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) doUpload(file, { docType: addType || undefined });
  };

  const current = (files || []).filter((f) => !f.superseded);
  const previous = (files || []).filter((f) => f.superseded);

  // Where a file that cannot be replaced here IS maintained. Returns null when
  // this file needs no guidance — an empty node would still take a gap in the
  // meta row, so the answer and "is there an answer" come from the same place.
  const refGuidance = (f) => {
    if (f.source === 'indep') {
      return <span className="text-xs text-slate-500">Generated from this engagement’s partner / director / manager / EQR, the setup approval date and the sign-off date</span>;
    }
    if (!f.referenced) return null;
    if (f.source === 'group') {
      return (
        <Link to="/settings/clients" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <ExternalLink className="w-3 h-3" /> Update on Clients &amp; Entities
        </Link>
      );
    }
    if (f.source === 'prev') {
      return <span className="text-xs text-slate-500">Update on last year’s engagement{f.priorEngagementName ? ` · ${f.priorEngagementName}` : ''}</span>;
    }
    if (f.source === 'eng' && f.docType === 'QC7_PREV') {
      return <span className="text-xs text-slate-500">Update via the Engagement Registration request{f.requestNo ? ` · ${f.requestNo}` : ''}</span>;
    }
    return null;
  };

  // Two lines per row: this panel's rows carry guidance sentences ("Update on
  // last year's engagement …") that will not sit beside the filename.
  const rowProps = (f, { muted } = {}) => {
    const src = ATTACHMENT_SOURCE[f.source] || { cls: 'bg-slate-100 text-slate-600' };
    const canVersion = !f.referenced && !muted;
    return {
      name: f.originalName,
      stacked: true,
      muted,
      badges: [
        [attachmentLabel(f), src.cls],
        f.referenced ? [f.source === 'indep' ? 'Generated' : 'Reference', 'bg-amber-50 text-amber-700'] : null,
      ],
      meta: [
        f.sizeBytes ? fmtBytes(f.sizeBytes) : null,
        refGuidance(f),
      ],
      actions: (
        <>
          <DownloadLink
            href={`${API_BASE_URL}/api/engagements/${engagementId}/attachments/${f.source}/${f.fileId}`}
            label={null}
            newTab
          />
          {canVersion && (
            <FilePicker kind="docsSheetsImages" buttonLabel="New Version" showSelection={false} disabled={busy}
              className="shrink-0" onChange={onNewVersion(f)} />
          )}
        </>
      ),
    };
  };

  const list = (rows, opts) => (
    <FileList
      files={rows}
      rowKey={(f) => `${f.source}-${f.fileId}`}
      className={opts?.muted ? 'mt-1' : ''}
      row={(f) => rowProps(f, opts)}
    />
  );

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <span className="text-sm font-medium text-slate-700">Attachments</span>
      </div>
      <div className="p-4 space-y-3">
        {error && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
            <span>{error}</span>
          </div>
        )}
        {files === null && <p className="text-sm text-slate-500 py-2">Loading…</p>}
        {files !== null && files.length === 0 && (
          <p className="text-sm text-slate-500 py-2">No attachments linked to this engagement.</p>
        )}

        {current.length > 0 && list(current)}

        {/* Previous versions — the superseded workflow originals / older own files.
            Kept for audit, download-only, collapsed by default. */}
        <PreviousVersions count={previous.length}>{list(previous, { muted: true })}</PreviousVersions>

        <div className="flex items-center gap-2 pt-1">
          <select value={addType} onChange={(e) => setAddType(e.target.value)} disabled={busy}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-700">
            {OWN_DOC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <FilePicker kind="docsSheetsImages" buttonLabel={busy ? 'Uploading…' : 'Add Attachment'} showSelection={false}
            disabled={busy} onChange={onAdd} />
        </div>
      </div>
    </div>
  );
};
export const DeleteModal = ({ engagement, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(`/api/engagements/${engagement.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Delete Engagement</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to delete <span className="font-semibold">{engagementDisplayName(engagement.engagementName, engagement.serviceDescription)}</span>
            {engagement.yearEnd ? ` (FYE ${fmtDate(engagement.yearEnd)})` : ''}? This action cannot be undone.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={confirmDelete} disabled={deleting} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 附件來源的徽章樣式與名稱。
const ATTACHMENT_SOURCE = {
  eng: { label: 'Setup', cls: 'bg-blue-50 text-blue-700' },
  fs: { label: 'Signed Financial Statements', cls: 'bg-emerald-50 text-emerald-700' },
  // QC35 — its own workflow.
  qc35: { label: 'QC35 Form', cls: 'bg-emerald-50 text-emerald-700' },
  // Group-audit structure diagram, surfaced from the client group.
  group: { label: 'Group structure', cls: 'bg-amber-50 text-amber-700' },
  // Previous-year QC, a live reference to last year's engagement.
  prev: { label: 'Previous year QC', cls: 'bg-amber-50 text-amber-700' },
  // Engagement-owned upload.
  own: { label: 'Uploaded', cls: 'bg-violet-50 text-violet-700' },
  // Independence Confirmation — generated on download, never stored.
  indep: { label: 'Independence Confirmation', cls: 'bg-sky-50 text-sky-700' },
};

const DOC_TYPE_LABELS = {
  QC6: 'QC6', QC7: 'QC7', QC7_PREV: 'Previous year QC', GROUP_STRUCTURE: 'Group structure',
  FS: 'Signed Financial Statements', QC35: 'QC35 Form', SUPPLEMENTARY: 'Supplementary',
  INDEPENDENCE_CONFIRMATION: 'Independence Confirmation',
  BIZFILE: 'BizFile', SCREENING: 'Screening',
};

// Badge text for one attachment. Setup (eng) files carry a doc_type
// so we show the real QC6 / QC7 / previous-year QC instead of a fixed
// "QC6/7"; legacy pre-split files (doc_type NULL) fall back to the generic
// label. Group structure and previous-year QC are live references (source
// 'group' / 'prev'); own files show their doc type. A legacy
// wf_fs row can carry a qc35 doc (pre-split) — label those by docType too.
const attachmentLabel = (f) => {
  if (f.source === 'group') return 'Group structure';
  if (f.source === 'prev') return 'Previous year QC';
  if (f.source === 'fs') return f.docType === 'qc35' ? 'QC35 Form' : 'Signed Financial Statements';
  if (f.source === 'eng') return f.docType ? DOC_TYPE_LABELS[f.docType] || f.docType : 'QC6/7';
  if (f.source === 'own') return f.docType ? DOC_TYPE_LABELS[f.docType] || f.docType : 'Uploaded';
  return ATTACHMENT_SOURCE[f.source]?.label || f.source;
};

export default QMEngagements;
